import React, { useRef, useState, useEffect, useCallback } from "react";
import "./EduEx.css";
import { RESUME_URL } from "../../constants";

/* ===== Theme colors (inline) ===== */
const theme = {
  highlight: "#e8dfd5",
  accent: "#ffd700",
  muted: "#7a6a8a",
  text: "#c4a882",
};

/* ==================== MILESTONES ==================== */
const milestones = [
  {
    type: "exp", icon: "🅰", title: "Member of Technical Staff 2",
    place: "Adobe (Document Cloud)", shortLabel: "MTS-2",
    // Sign board in the 3D scene truncates `title` at 20 chars — use a short form there
    signLabel: "MTS-2 @ Adobe",
    year: "Aug 2026 – Present", detail: "Document Cloud", color: "#fa0f00",
    points: [
      "Joined <b>Adobe</b> as a <b>Member of Technical Staff 2</b> on the <b>Document Cloud</b> team",
      "Arrived after <b>3 years at Infoedge (Naukri Campus)</b> shipping products used by <b>200K+ users</b>",
      "<b>LeetCode Guardian (2244)</b> · <b>GATE'21 AIR 2547</b> · open-source contributor",
    ],
    tags: ["TypeScript", "React", "Java", "System Design"],
    stats: [
      { value: "MTS-2", label: "Member of Technical Staff" },
      { value: "2026", label: "Year Joined" },
    ],
  },
  {
    type: "exp", icon: "⚡", title: "Senior Software Engineer",
    place: "Infoedge (Naukri Campus)", shortLabel: "Sr. SWE",
    year: "Jul 2023 – Aug 2026", detail: "200K+ users · Awards", color: "#8b5cf6",
    points: [
      "<b>Led recruiter-side frontend architecture</b> for Naukri Campus, enabling scalable hiring workflows for <b>10K+ college freshers</b>",
      "Architected and <b>standardized 6+ reusable React patterns</b> (feature-based modular structure, custom hooks abstraction)",
      "<b>Shipped 10+ production-grade features</b> across contests, engagement, and assessment platforms, <b>impacting 200K+ users</b>",
      "Drove <b>performance optimization across 10+ high-traffic pages</b>, improving Core Web Vitals (<b>INP by 38%</b> and <b>CLS by 95%</b>)",
      "Built <b>recruiter subscription and quota-distribution APIs</b>, automating refunds across 3+ products, supporting <b>10K+ transactions</b>",
      "Recognized with <b>5-Star Annual Rating</b>, <b>Quarterly Individual Excellence Award</b>, and <b>Team Collaboration Award</b>",
    ],
    tags: ["TypeScript", "React", "Sass", "Java", "SpringBoot", "Kafka"],
    stats: [
      { value: "200K+", label: "Users Impacted" },
      { value: "10+", label: "Features Shipped" },
      { value: "38%", label: "INP Improved" },
      { value: "95%", label: "CLS Improved" },
      { value: "⭐ 5", label: "Star Annual Rating" },
      { value: "🏆", label: "Individual Excellence Award" },
    ],
  },
  {
    type: "exp", icon: "🚀", title: "Software Engineer Intern",
    place: "Infoedge (Naukri Campus)", shortLabel: "Intern",
    year: "Jan 2023 – Jun 2023", detail: "AI Resume · RN/Flutter", color: "#e74c3c",
    points: [
      "Spearheaded <b>AI-powered resume summary system</b> leveraging LLM-based content generation, <b>improving resume quality by 20%</b>",
      "<b>Migrated 7+ React components</b> to React Native and Flutter, enabling <b>cross-platform compatibility</b>",
      "Evaluated <b>cross-platform performance</b> (RN vs Flutter), found <b>2.7x higher CPU utilization</b> in React Native",
    ],
    tags: ["React", "React Native", "Flutter", "AI/LLM"],
    stats: [
      { value: "+20%", label: "Resume Quality" },
      { value: "7+", label: "Components Migrated" },
      { value: "2.7×", label: "CPU RN vs Flutter" },
    ],
  },
  {
    type: "edu", icon: "🎓", title: "MCA (Master of Computer Application)",
    place: "University of Delhi", shortLabel: "MCA",
    year: "Jul 2020 – May 2023", detail: "CGPA: 8.365/10", color: "#ffd700",
    cgpa: 8.365,
    points: [
      "Master's in Computer Application with focus on <b>advanced CS</b>",
      "Deepened expertise in <b>System Design, ML, and Distributed Systems</b>",
      "<b>GATE 2021 Qualified — AIR 2547</b>, Top 3% among <b>100K+ candidates</b>",
    ],
    tags: ["System Design", "Algorithms", "Machine Learning", "GATE"],
    stats: [
      { value: "8.365", label: "CGPA / 10" },
      { value: "2547", label: "GATE AIR" },
      { value: "Top 3%", label: "Percentile" },
    ],
  },
  {
    type: "exp", icon: "⚛️", title: "Open Source Contributor",
    place: "React Native Elements", shortLabel: "RNE",
    year: "Jan 2021 – Jul 2021", detail: "10+ PRs · 5+ Bugs", color: "#00cc66",
    points: [
      "Standardized code formatting, <b>contributing 10+ merged PRs</b> using ESLint and Prettier, reducing integration conflicts",
      "<b>Resolved 5+ bugs</b> and developed <b>3+ playground environments</b>, enhancing UI stability",
    ],
    tags: ["TypeScript", "React Native", "MDX"],
    stats: [
      { value: "10+", label: "PRs Merged" },
      { value: "5+", label: "Bugs Fixed" },
      { value: "3+", label: "Playgrounds Built" },
    ],
  },
  {
    type: "exp", icon: "🧱", title: "Open Source · Rank 6",
    place: "Coding Blocks (BOSS'20)", shortLabel: "BOSS'20",
    year: "May 2020 – Jul 2020", detail: "12 PRs merged", color: "#ff6b00",
    points: [
      "<b>Ranked 6th in BOSS'20</b>, contributing <b>12 merged pull requests</b> across platform features",
      "Built <b>reusable Error Layout components</b> and <b>resolved 5+ bugs</b> across core user flows",
      "Engineered <b>secure user-impersonation for admins</b>, achieving <b>90% test coverage</b>",
    ],
    tags: ["JavaScript", "React", "GraphQL", "CSS"],
    stats: [
      { value: "6th", label: "BOSS'20 Rank" },
      { value: "12", label: "PRs Merged" },
      { value: "90%", label: "Test Coverage" },
    ],
  },
  {
    type: "edu", icon: "🎓", title: "B.Sc (Hons.) Computer Science",
    place: "Shaheed Sukhdev College, University of Delhi", shortLabel: "B.Sc CS",
    year: "Jul 2016 – May 2019", detail: "CGPA: 8.55/10", color: "#00bcd4",
    cgpa: 8.55,
    points: [
      "Three-year undergraduate program in <b>Computer Science</b>",
      "Covered <b>Data Structures, Algorithms, OS, DBMS, Networks</b>",
      "<b>DU Entrance — AIR 13</b> among <b>20K+ candidates</b>",
    ],
    tags: ["C++", "Java", "Data Structures", "Algorithms", "DBMS"],
    stats: [
      { value: "8.55", label: "CGPA / 10" },
      { value: "AIR 13", label: "DU Entrance" },
      { value: "20K+", label: "Candidates" },
    ],
  },
  {
    type: "exp", icon: "🥷", title: "Teaching Assistant",
    place: "Coding Ninjas", shortLabel: "TA",
    year: "Jan 2019 – Sep 2019", detail: "DSA Python · 1500+ doubts", color: "#f5a623",
    points: [
      "Conducted <b>1-on-1 doubt sessions</b>, <b>solved over 1500 doubts</b> across two batches",
      "Debugged <b>2000+ errors/bugs</b> in Data Structures and Algorithms (Python)",
    ],
    tags: ["Python", "DSA", "Teaching"],
    stats: [
      { value: "1500+", label: "Doubts Solved" },
      { value: "2000+", label: "Bugs Debugged" },
      { value: "60+", label: "Students Mentored" },
    ],
  },
];

function hasWebGL() {
  try {
    const c = document.createElement("canvas");
    return !!(c.getContext("webgl") || c.getContext("webgl2"));
  } catch { return false; }
}

/* ==================== COMPONENT ==================== */
const Experience: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<any>(null);
  const [activeStop, setActiveStop] = useState<any>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [webglOk] = useState(hasWebGL);
  const [direction, setDirection] = useState<"forward" | "backward" | null>(null);
  const [loading, setLoading] = useState(true);
  const prevIndexRef = useRef(-1);

  const onStopChange = useCallback((milestone: any, index: number) => {
    setActiveStop(milestone);
    setActiveIndex((prev) => {
      if (index >= 0 && prev >= 0 && index !== prev) {
        // Milestones are ordered latest-first, so higher index = going backward in time
        setDirection(index > prev ? "backward" : "forward");
      }
      prevIndexRef.current = index;
      return index;
    });
    if (milestone !== null) setReachedEnd(false);
  }, []);

  const onReachEnd = useCallback(() => setReachedEnd(true), []);

  useEffect(() => {
    if (!webglOk || !containerRef.current) return;
    let scene: any = null;
    let disposed = false;

    import("./three/RoadScene").then(({ default: RoadScene }) => {
      if (disposed) return;
      scene = new RoadScene(containerRef.current, milestones, { onStopChange, onReachEnd });
      sceneRef.current = scene;
      // Give scene a moment to render first frame
      setTimeout(() => { if (!disposed) setLoading(false); }, 800);
    });

    return () => {
      disposed = true;
      if (scene) scene.dispose();
      sceneRef.current = null;
    };
  }, [webglOk, onStopChange, onReachEnd]);

  return (
    <div className="eduex-page" id="experience">
      {/* 3D Road Scene — full viewport from top */}
      {webglOk ? (
        <div className="road3d-wrap road3d-wrap-full">
          <div ref={containerRef} className="road3d-container" />

          {/* Loader */}
          {loading && (
            <div className="road3d-loader">
              <div className="road3d-loader-train">🚂</div>
              <div className="road3d-loader-text">Boarding the Hogwarts Express...</div>
              <div className="road3d-loader-bar"><div className="road3d-loader-fill" /></div>
            </div>
          )}

          {/* Resume ribbon — left side, only in experience */}
          <a
            className="exp-resume-ribbon"
            href={RESUME_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="exp-ribbon-thread" />
            <span className="exp-ribbon-body">
              {"RESUME".split("").map((ch, i) => (
                <span key={i} className="exp-ribbon-char">{ch}</span>
              ))}
              <span className="exp-ribbon-arrow">↓</span>
            </span>
            <span className="exp-ribbon-tail" />
          </a>

          {/* Left-bottom — heading + platform sign */}
          <div className="road3d-overlay-left-bottom">
            <div className="eduex-platform-sign eduex-platform-sign-small">
              <span className="eduex-platform-num">9¾</span>
              <span className="eduex-platform-text">Platform</span>
            </div>
            <span className="road3d-overlay-overline" style={{ color: theme.accent }}>THE JOURNEY</span>
            <h1 className="road3d-overlay-title" style={{ color: theme.highlight }}>
              From Classrooms to<br /><span style={{ color: theme.accent }}>Production Codebases</span>
            </h1>
            <div className="road3d-controls-hint">
              <span className="road3d-hint-keys">
                <span className="road3d-hint-key">↑</span>
                <span className="road3d-hint-key">↓</span>
                <span className="road3d-hint-label">to drive</span>
              </span>
              <span className="road3d-hint-divider">·</span>
              <span className="road3d-hint-label">click dots to jump</span>
            </div>
          </div>

          {/* Direction indicator — only for backward */}
          {direction === "backward" && (
            <div className="road3d-direction road3d-direction-backward" key={"back" + activeIndex}>
              <span className="road3d-dir-icon">⏳</span>
              <span className="road3d-dir-text">Turning Back the Time-Turner...</span>
              <span className="road3d-dir-sub">Revisiting the past</span>
            </div>
          )}

          {/* Right-top — milestone info card */}
          <div className={`road3d-info-card ${activeStop ? "road3d-card-visible" : ""}`} style={activeStop ? { "--glow-color": activeStop.color } as React.CSSProperties : undefined}>
            {activeStop && (
              <>
                <div className="road3d-top-row">
                  <span className="road3d-type-tag" style={{ background: activeStop.color + "20", borderColor: activeStop.color + "40", color: activeStop.color }}>
                    {activeStop.icon} {activeStop.type === "edu" ? "Education" : "Experience"}
                  </span>
                  <span className="road3d-year-badge" style={{ color: theme.muted }}>
                    {activeStop.year}
                  </span>
                </div>
                <h3 className="road3d-detail-title" style={{ color: theme.highlight }}>
                  {activeStop.title}
                </h3>
                <p className="road3d-detail-place" style={{ color: activeStop.color }}>
                  {activeStop.place}
                </p>
                {activeStop.cgpa && (
                  <div className="road3d-cgpa">
                    <div className="road3d-cgpa-header">
                      <span style={{ color: theme.muted }}>CGPA</span>
                      <span style={{ color: theme.highlight }}>{activeStop.cgpa}/10</span>
                    </div>
                    <div className="road3d-cgpa-bar">
                      <div className="road3d-cgpa-fill" style={{
                        width: `${(activeStop.cgpa / 10) * 100}%`,
                        background: `linear-gradient(90deg, ${activeStop.color}88, ${activeStop.color})`,
                      }} />
                    </div>
                  </div>
                )}
                <ul className="road3d-points">
                  {activeStop.points.map((pt: string, i: number) => (
                    <li key={i} className="road3d-point" style={{ color: theme.text }}>
                      <span className="road3d-point-dot" style={{ background: activeStop.color }} />
                      <span dangerouslySetInnerHTML={{ __html: pt }} />
                    </li>
                  ))}
                </ul>
                <div className="road3d-detail-tags">
                  {activeStop.tags.map((t: string, i: number) => (
                    <span key={i} className="road3d-tag">{t}</span>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Right-bottom — stats card */}
          <div className={`road3d-stats-card ${activeStop ? "road3d-card-visible" : ""}`} style={activeStop ? { "--glow-color": activeStop.color } as React.CSSProperties : undefined}>
            {activeStop && activeStop.stats && (
              <>
                <h4 className="road3d-stats-title" style={{ color: theme.muted }}>Highlights</h4>
                <div className="road3d-stats-grid">
                  {activeStop.stats.map((s: any, i: number) => (
                    <div key={i} className="road3d-stat-box" style={{ borderColor: activeStop.color + "25" }}>
                      <span className="road3d-stat-value" style={{ color: activeStop.color }}>{s.value}</span>
                      <span className="road3d-stat-label" style={{ color: theme.muted }}>{s.label}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Timeline dots */}
          <div className="road3d-timeline">
            {milestones.map((m: any, i: number) => (
              <div
                key={i}
                className={`road3d-dot ${i === activeIndex ? "road3d-dot-active" : ""}`}
                style={{ "--dot-color": m.color } as React.CSSProperties}
                title={m.title}
                onClick={() => sceneRef.current && sceneRef.current.jumpTo(i)}
              >
                <span className="road3d-dot-inner" />
                <span className="road3d-dot-label">{m.shortLabel}</span>
              </div>
            ))}
            <div className="road3d-dot road3d-dot-soon" title="More coming soon">
              <span className="road3d-dot-inner" />
              <span className="road3d-dot-inner" />
              <span className="road3d-dot-inner" />
            </div>
          </div>

          {/* End message */}
          {reachedEnd && activeIndex === -1 && (
            <div className="road3d-coming-soon">
              <span className="road3d-coming-icon">⚡</span>
              <span className="road3d-coming-text" style={{ color: theme.highlight }}>
                The next chapter awaits...
              </span>
              <span className="road3d-coming-sub" style={{ color: theme.muted }}>
                — The journey continues
              </span>
            </div>
          )}

          {/* hint is in the right overlay */}
        </div>
      ) : (
        <div style={{ padding: "40px 6%", textAlign: "center", color: "#5a4a6a" }}>
          <p>WebGL not supported. Please use a modern browser.</p>
        </div>
      )}
    </div>
  );
};

export default Experience;
